import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

const BASIC_PLAN_PRICE_ID = 'price_1QlVASJ17IHshykoCx0B31xj';
const STANDARD_PLAN_PRICE_ID = 'price_1QlVASJ17IHshykoTGRVSSym';

// Map Stripe price IDs to OneInbox plan IDs
const PLAN_ID_MAPPING = {
  [BASIC_PLAN_PRICE_ID]: 117, // Basic plan ID //109 prod
  [STANDARD_PLAN_PRICE_ID]: 118, // Standard plan ID //110 prod
};

const PLAN_REDIRECT_MAPPING = {
  117: '/time-preference',
  118: '/time-preference-2',
};


const Plans = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [isProcessingSuccess, setIsProcessingSuccess] = useState(false);

  useEffect(() => {
    const processSuccessfulPurchase = async (stripePriceId: string) => {
      setIsProcessingSuccess(true); // Show processing indicator
      console.log("Processing successful purchase for Stripe Price ID:", stripePriceId);

      const oneInboxPlanId = PLAN_ID_MAPPING[stripePriceId];
      if (!oneInboxPlanId) {
        console.error("Invalid Stripe Price ID - OneInbox Plan ID mapping:", stripePriceId);
        toast({ title: "Error", description: "Invalid plan selection after payment.", variant: "destructive" });
        setIsProcessingSuccess(false);
        return;
      }

      // --- Get subscriber_id ---
      // Option 1 (Best): Retrieve from Stripe checkout session metadata if you set it
      // const sessionId = searchParams.get('session_id'); // Assuming Stripe returns session_id
      // if (sessionId) { /* Fetch session from your backend to get metadata */ }
      // Option 2 (Fallback): Get from localStorage (set during registration)
      const subscriberId = localStorage.getItem('subscriber_id');
      if (!subscriberId) {
          console.error("Could not find subscriber_id in localStorage after payment success.");
          toast({ title: "Error", description: "Could not identify your account after payment. Please contact support.", variant: "destructive"});
          setIsProcessingSuccess(false);
          return;
      }
      console.log("Found subscriber_id:", subscriberId, "Mapped OneInbox Plan ID:", oneInboxPlanId);

      try {
        console.log("Updating plan directly...");
        // --- Invoke the handle-payment-success function ---
        console.log("Invoking handle-payment-success function...");
        const { data, error } = await supabase.functions.invoke('handle-payment-success', {
          body: {
            subscriber_id: subscriberId,
            oneinbox_plan_id: oneInboxPlanId
          }
        });

        if (error) {
           console.error("Error invoking handle-payment-success:", error);
           console.log("Continuing with redirect despite error...");
           // Don't throw error here, continue with redirect
        } else {
          console.log("handle-payment-success function returned:", data);
        }

        toast({
          title: "Success!",
          description: "Subscription activated & setup initiated. You will be notified when ready.",
        });

        const redirectPath = PLAN_REDIRECT_MAPPING[oneInboxPlanId] || '/call-history';
        console.log(`Redirecting to ${redirectPath} based on plan ID ${oneInboxPlanId}`);
        navigate(redirectPath);

      } catch (error: any) {
        console.error('Error in processing successful purchase:', error);
        toast({
          title: "Setup Error",
          description: error.message || "Failed to initiate resource setup. Please contact support.",
          variant: "destructive",
        });
      } finally {
         setIsProcessingSuccess(false);
      }
    };

    // Check URL params from Stripe redirect
    if (searchParams.get('success') === 'true') {
      const priceId = searchParams.get('price_id'); // Get price_id if returned by Stripe redirect
      if (priceId) {
        processSuccessfulPurchase(priceId);
        // Optional: Clear URL params after processing
        // navigate('/plans', { replace: true });
      } else {
         console.warn("Success redirect missing price_id parameter.");
         toast({ title: "Warning", description: "Payment successful, but couldn't identify plan. Contact support if setup fails.", variant: "default"});
         navigate('/call-history');
        }
    } else if (searchParams.get('canceled') === 'true') {
      toast({ description: "Payment was canceled.", variant: "destructive" });
    }
  }, [searchParams, toast, navigate]);

  const handleSubscribe = async (priceId: string) => {
    try {
      setLoadingPlan(priceId);
      console.log('Initiating subscription for price:', priceId);
       // Get current user to potentially pass ID to checkout if needed
       const { data: { session } } = await supabase.auth.getSession();
       const user = session?.user;
       const subscriberId = localStorage.getItem('subscriber_id');
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId }
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw error;
      }

      if (data?.url) {
        console.log('Redirecting to checkout URL:', data.url);
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Subscription Error:', error);
      toast({
        title: "Error",
        description: "Failed to process subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      {isProcessingSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
            <Loader2 className="h-8 w-8 animate-spin mb-4" />
            <p className="text-lg">Processing your subscription...</p>
          </div>
        </div>
      )}
      <h1 className="text-3xl font-semibold text-center mb-12">
        Choose the plan that's right for you
      </h1>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl w-full">
        {/* Basic Plan */}
        <Card className="p-6 bg-white rounded-lg shadow-sm">
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold">Basic Plan</h2>
            <p className="text-gray-600">One Call Per Day</p>
            <p className="text-3xl font-bold">$29.99/month</p>
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => handleSubscribe(BASIC_PLAN_PRICE_ID)}
              disabled={loadingPlan !== null}
            >
              {loadingPlan === BASIC_PLAN_PRICE_ID ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Buy Starter'
              )}
            </Button>
          </div>
        </Card>

        {/* Standard Plan */}
        <Card className="p-6 bg-purple-50 rounded-lg shadow-sm relative">
          <div className="absolute -top-3 right-4 bg-purple-100 text-purple-600 px-3 py-1 rounded-full text-sm">
            Most popular
          </div>
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold">Standard Plan</h2>
            <p className="text-gray-600">Two Calls Per Day</p>
            <p className="text-3xl font-bold">$49.99/month</p>
            <Button 
              className="w-full mt-4 bg-black hover:bg-gray-800"
              onClick={() => handleSubscribe(STANDARD_PLAN_PRICE_ID)}
              disabled={loadingPlan !== null}
            >
              {loadingPlan === STANDARD_PLAN_PRICE_ID ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Buy Pro'
              )}
            </Button>
          </div>
        </Card>
      </div>

      <div className="mt-8 flex gap-4 text-sm text-gray-600">
        <a href="#" className="hover:text-gray-900">Terms of Service</a>
        <a href="#" className="hover:text-gray-900">Privacy Policy</a>
      </div>
    </div>
  );
};

export default Plans;
